import { BreakDefinitionInput, RunSettingsInput } from 'core/graphql/types';
import {
  BreakDraft,
  defaultRunSettingsDraft,
  RunSettingsDraft,
} from 'core/store/CapacityPlanningSlice';
import {
  hhMmToTimeSpan,
  timeSpanToHhMm,
  timeSpanToSeconds,
} from 'core/utils/TimeSpanUtils';

/**
 * The Run Settings carried by a Run, whatever query selected it - i.e. the
 * `settings` object nested on a `CapacityPlanRun`. The Planning Depot is
 * optional: a Network Run has none, and neither has an Agency Run whose file
 * carries the Depot columns (ADR-0001).
 */
export type RunSettingsSource = {
  depotLatitude?: number | null;
  depotLongitude?: number | null;
  shiftStart: string;
  shiftEnd: string;
  breaks: Array<{
    windowStart: string;
    windowEnd: string;
    durationSeconds: number;
  }>;
};

export type PlanningDepot = {
  latitude?: number | null;
  longitude?: number | null;
};

export const breakDurationMinutesRange = { min: 1, max: 480 };
export const labelMaxLength = 100;

const coordinate = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : `${value}`;

export function runToDraft(
  settings: RunSettingsSource,
  label?: string | null,
): RunSettingsDraft {
  return {
    label: label ?? '',
    depotLatitude: coordinate(settings.depotLatitude),
    depotLongitude: coordinate(settings.depotLongitude),
    shiftStart: timeSpanToHhMm(settings.shiftStart),
    shiftEnd: timeSpanToHhMm(settings.shiftEnd),
    breaks: settings.breaks.map((item) => ({
      windowStart: timeSpanToHhMm(item.windowStart),
      windowEnd: timeSpanToHhMm(item.windowEnd),
      durationMinutes: `${Math.round(item.durationSeconds / 60)}`,
    })),
  };
}

/**
 * Prefill for a Run that does not exist yet: the newest Run's settings,
 * falling back to the nominal defaults with the Planning Depot taken from the
 * agency Depot synced from LM. The Label always starts empty - the New run
 * form fills it from the picked file's name.
 */
export function prefillDraft(
  newestSettings: RunSettingsSource | undefined,
  depot: PlanningDepot | undefined,
): RunSettingsDraft {
  if (newestSettings) {
    return runToDraft(newestSettings);
  }

  return {
    ...defaultRunSettingsDraft,
    depotLatitude: coordinate(depot?.latitude),
    depotLongitude: coordinate(depot?.longitude),
  };
}

export type DraftConversion =
  | { ok: true; settings: RunSettingsInput }
  | { ok: false; message: string };

export type DraftConversionOptions = {
  /** A Network Run has no Planning Depot: the fields are hidden and sent null. */
  isNetworkRun?: boolean;
};

const invalid = (message: string): DraftConversion => ({ ok: false, message });

const parseInteger = (value: string) =>
  /^-?\d+$/.test(value.trim()) ? Number(value.trim()) : Number.NaN;

const parseCoordinate = (value: string) => {
  const trimmed = value.trim();
  return /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : Number.NaN;
};

type BreakConversion =
  | { ok: true; value: BreakDefinitionInput }
  | { ok: false; message: string };

function convertBreak(
  draft: BreakDraft,
  index: number,
  shiftStartSeconds: number,
  shiftEndSeconds: number,
): BreakConversion {
  const windowStart = timeSpanToSeconds(draft.windowStart);
  const windowEnd = timeSpanToSeconds(draft.windowEnd);
  const durationMinutes = parseInteger(draft.durationMinutes);
  const label = `Break ${index + 1}`;

  if (windowStart === null || windowEnd === null) {
    return { ok: false, message: `${label} needs a start and an end time.` };
  }
  if (windowStart >= windowEnd) {
    return { ok: false, message: `${label} must start before it ends.` };
  }
  if (
    !Number.isInteger(durationMinutes) ||
    durationMinutes < breakDurationMinutesRange.min ||
    durationMinutes > breakDurationMinutesRange.max
  ) {
    return {
      ok: false,
      message: `${label} duration must be between ${breakDurationMinutesRange.min} and ${breakDurationMinutesRange.max} minutes.`,
    };
  }
  if (windowStart < shiftStartSeconds || windowEnd > shiftEndSeconds) {
    return {
      ok: false,
      message: `${label} must fall inside the shift window.`,
    };
  }
  if (windowStart + durationMinutes * 60 > shiftEndSeconds) {
    return {
      ok: false,
      message: `${label} cannot finish after the end of the shift.`,
    };
  }

  return {
    ok: true,
    value: {
      windowStart: hhMmToTimeSpan(draft.windowStart)!,
      windowEnd: hhMmToTimeSpan(draft.windowEnd)!,
      durationSeconds: durationMinutes * 60,
    },
  };
}

type DepotConversion =
  | { ok: true; latitude: number | null; longitude: number | null }
  | { ok: false; message: string };

/**
 * The Planning Depot is either both coordinates or neither, exactly as the
 * API's `RunSettingsInputValidator` reads it.
 */
function convertDepot(
  draft: RunSettingsDraft,
  isNetworkRun: boolean,
): DepotConversion {
  if (isNetworkRun) {
    return { ok: true, latitude: null, longitude: null };
  }

  const hasLatitude = draft.depotLatitude.trim() !== '';
  const hasLongitude = draft.depotLongitude.trim() !== '';

  if (!hasLatitude && !hasLongitude) {
    return { ok: true, latitude: null, longitude: null };
  }
  if (hasLatitude !== hasLongitude) {
    return {
      ok: false,
      message: 'The Planning Depot needs both a latitude and a longitude.',
    };
  }

  const latitude = parseCoordinate(draft.depotLatitude);
  const longitude = parseCoordinate(draft.depotLongitude);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return {
      ok: false,
      message: 'The Planning Depot coordinates must be numbers.',
    };
  }
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return {
      ok: false,
      message: 'The Planning Depot coordinates are out of range.',
    };
  }

  return { ok: true, latitude, longitude };
}

/**
 * Validates the draft the way the API's FluentValidation does, so the
 * supervisor sees the problem before the round trip.
 */
export function draftToSettings(
  draft: RunSettingsDraft,
  { isNetworkRun = false }: DraftConversionOptions = {},
): DraftConversion {
  const depot = convertDepot(draft, isNetworkRun);
  const shiftStartSeconds = timeSpanToSeconds(draft.shiftStart);
  const shiftEndSeconds = timeSpanToSeconds(draft.shiftEnd);
  const label = draft.label.trim();

  if (!depot.ok) {
    return invalid(depot.message);
  }
  if (label.length > labelMaxLength) {
    return invalid(`The label must be at most ${labelMaxLength} characters.`);
  }
  if (shiftStartSeconds === null || shiftEndSeconds === null) {
    return invalid('The shift window needs a start and an end time.');
  }
  if (shiftStartSeconds >= shiftEndSeconds) {
    return invalid('The shift must start before it ends.');
  }

  const breaks: RunSettingsInput['breaks'] = [];
  for (let index = 0; index < draft.breaks.length; index += 1) {
    const converted = convertBreak(
      draft.breaks[index],
      index,
      shiftStartSeconds,
      shiftEndSeconds,
    );
    if (!converted.ok) {
      return invalid(converted.message);
    }
    breaks.push(converted.value);
  }

  return {
    ok: true,
    settings: {
      depotLatitude: depot.latitude,
      depotLongitude: depot.longitude,
      shiftStart: hhMmToTimeSpan(draft.shiftStart)!,
      shiftEnd: hhMmToTimeSpan(draft.shiftEnd)!,
      label: label === '' ? null : label,
      breaks,
    },
  };
}

/** The draft fields compared one by one; `breaks` counts as a single field. */
const comparedFields: Array<keyof Omit<RunSettingsDraft, 'breaks'>> = [
  'label',
  'depotLatitude',
  'depotLongitude',
  'shiftStart',
  'shiftEnd',
];

/**
 * How many settings the supervisor changed since the Run was loaded, so the
 * unsaved-changes bar can say "2 unsaved changes". The whole list of Breaks
 * counts as one change.
 */
export function countChanges(
  draft: RunSettingsDraft,
  base: RunSettingsDraft,
): number {
  const changedFields = comparedFields.filter(
    (field) => draft[field] !== base[field],
  ).length;
  const changedBreaks =
    JSON.stringify(draft.breaks) === JSON.stringify(base.breaks) ? 0 : 1;

  return changedFields + changedBreaks;
}
